import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Building2, Save, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { getCompanyProfile, updateCompanyProfile } from '../../services/api';

export const CompanyProfileCard = ({ readOnly = false }) => {
  const [name, setName] = useState('');
  const [defaultName, setDefaultName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await getCompanyProfile();
      setName(res.data.name || '');
      setDefaultName(res.data.default_name || '');
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load company name', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Enter a company name', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await updateCompanyProfile(name.trim());
      setName(res.data.name);
      toast({ title: 'Saved', description: 'Company name updated on all documents' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save company name', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4" data-testid="company-profile-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-teal-600" /> Company Name (Documents)
        </CardTitle>
        <CardDescription>
          This name appears on every generated document — payslips, leave-salary, EOSB and part-timer slips.
          Change it once here and it updates everywhere.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 max-w-3xl">
          <div className="flex-1">
            <Label htmlFor="company-name-input" className="text-sm">Company name</Label>
            <Input
              id="company-name-input"
              data-testid="company-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={readOnly || loading}
              placeholder="e.g. MONACO STARS AND BARS RESTAURANT LLC"
              className="mt-1"
            />
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving || loading} data-testid="company-name-save-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
              </Button>
              {defaultName && (
                <Button variant="outline" onClick={() => setName(defaultName)} disabled={saving} data-testid="company-name-reset-btn" title="Use default">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyProfileCard;
